import ReactMarkdown from 'react-markdown';

const COMPARISON_MARKDOWN_COMPONENTS = {
  p: ({ children }) => <p className="mb-4 last:mb-0 leading-relaxed">{children}</p>,
  ul: ({ children }) => <ul className="mb-4 last:mb-0 list-disc space-y-2 pl-4">{children}</ul>,
  ol: ({ children }) => <ol className="mb-4 last:mb-0 list-decimal space-y-2 pl-4">{children}</ol>,
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-white">{children}</strong>,
  code: ({ inline, children }) =>
    inline ? (
      <code className="rounded bg-zinc-800 px-1.5 py-0.5 font-mono text-sm text-zinc-200">{children}</code>
    ) : (
      <code className="mb-4 block overflow-x-auto rounded-sm bg-zinc-900 p-3 font-mono text-sm">{children}</code>
    ),
  pre: ({ children }) => <pre className="mb-4 last:mb-0">{children}</pre>,
};

function ComparisonStatusCard({ testId, className, title, children }) {
  return (
    <div data-testid={testId} className={className}>
      <div className="font-medium">{title}</div>
      {children}
    </div>
  );
}

function ComparisonVoteButton({ modelKey, isPreferred, onVote, isVoting }) {
  const buttonClass = isPreferred
    ? 'bg-white text-black'
    : 'border border-zinc-700 bg-zinc-900 text-zinc-300 hover:border-white hover:text-white';

  return (
    <button
      type="button"
      data-testid={`prefer-response-button-${modelKey}`}
      disabled={isVoting}
      onClick={() => onVote(modelKey)}
      className={`rounded-full px-3 py-1.5 text-xs transition-colors ${buttonClass} disabled:opacity-60`}
    >
      {isPreferred ? 'Réponse préférée' : 'Je préfère cette réponse'}
    </button>
  );
}

function ComparisonContent({ content }) {
  return (
    <div className="prose prose-invert prose-sm max-w-none break-words">
      <ReactMarkdown components={COMPARISON_MARKDOWN_COMPONENTS}>{content}</ReactMarkdown>
    </div>
  );
}

function renderComparisonBody(response) {
  if (!response.success) {
    return (
      <ComparisonStatusCard
        testId={`comparison-card-error-${response.model_key}`}
        className="rounded-xl border border-red-900/50 bg-red-950/30 p-4 text-sm text-red-200"
        title="Réponse indisponible"
      >
        <div className="mt-2 text-xs text-red-300/80">{response.error || 'Erreur inconnue.'}</div>
      </ComparisonStatusCard>
    );
  }

  if (response.content) {
    return <ComparisonContent content={response.content} />;
  }

  return (
    <ComparisonStatusCard
      testId={`comparison-card-empty-${response.model_key}`}
      className="rounded-xl border border-amber-900/50 bg-amber-950/30 p-4 text-sm text-amber-100"
      title="Réponse vide"
    >
      <div className="mt-2">
        Le modèle a répondu sans contenu exploitable. Réessaie la comparaison ou utilise momentanément les deux autres colonnes.
      </div>
    </ComparisonStatusCard>
  );
}

export default function ComparisonResponseCard({ response, isPreferred, onVote, isVoting }) {
  return (
    <div
      data-testid={`comparison-card-${response.model_key}`}
      className={`min-w-0 rounded-2xl border p-4 transition-colors md:p-5 ${
        isPreferred ? 'border-white bg-zinc-900' : 'border-zinc-800 bg-zinc-950/70'
      }`}
    >
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-white">{response.label}</div>
          <div className="text-xs text-zinc-500">{response.model_name}</div>
        </div>
        {response.success ? (
          <ComparisonVoteButton
            modelKey={response.model_key}
            isPreferred={isPreferred}
            onVote={onVote}
            isVoting={isVoting}
          />
        ) : null}
      </div>

      {renderComparisonBody(response)}
    </div>
  );
}
